import React, { useMemo } from "react";
import OverlayResultBox from "./OverlayResultBox";
import OverlayText from "./OverlayText";
import {
    getResultBackground,
    getResultLayout,
    normalizeLO,
    normalizeLOColumnPairs,
    normalizeLOValueBuckets,
    normalizePosts,
} from "./resultConfig";
import { format12h, getDate } from "../../utility/dateUtils";

const isTrue = (value) => value === true || value === "true";
const isAGroupPost = (post) => post === "A" || /^A\d+$/.test(String(post));

const getAGroupIndex = (post) => {
    if (post === "A") return 1;

    const match = String(post || "").match(/\d+/);
    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const getLoNumber = (post) => {
    const match = String(post || "").match(/\d+/);
    return match ? Number(match[0]) : 0;
};

const toVerticalColumns = (items = [], columns) => {
    if (columns !== 2 || items.length <= 1) return items;

    const firstColumnCount = Math.ceil(items.length / 2);
    const firstColumn = items.slice(0, firstColumnCount);
    const secondColumn = items.slice(firstColumnCount);
    const arrangedItems = [];

    for (let index = 0; index < firstColumnCount; index += 1) {
        arrangedItems.push(firstColumn[index]);

        if (secondColumn[index]) {
            arrangedItems.push(secondColumn[index]);
        }
    }

    return arrangedItems;
};

const toPairItem = (item = {}) => ({
    post: item.post,
    twoD: item.twoDigit,
    threeD: item.threeDigit,
});

const selectAGroupItems = (items, preferredCodes = [], limit) => {
    const itemMap = items.reduce((acc, item) => {
        acc[item.post] = item;
        return acc;
    }, {});

    const preferredItems = preferredCodes
        .map((code) => itemMap[code])
        .filter(Boolean);

    if (preferredItems.length) {
        return typeof limit === "number"
            ? preferredItems.slice(0, limit)
            : preferredItems;
    }

    return typeof limit === "number" ? items.slice(0, limit) : items;
};

const resolvePlatformTypography = (style = {}, platformKey = "other") => {
    if (!style) return style;

    const { platformAdjust, ...baseStyle } = style;
    const platformStyle = platformAdjust?.[platformKey] || {};

    return {
        ...baseStyle,
        ...platformStyle,
    };
};

export default function ResultImage({ drawing, lotteryType, platformKey = "other", brand = "p99" }) {
    const isNight = isTrue(drawing?.isNight);

    const posts = useMemo(
        () => normalizePosts(drawing?.drawItems?.POST || []),
        [drawing]
    );

    const loItems = useMemo(
        () =>
            normalizeLO(drawing?.drawItems?.LO || []).sort(
                (a, b) => getLoNumber(a.post) - getLoNumber(b.post)
            ),
        [drawing]
    );

    const loValueBuckets = useMemo(
        () => normalizeLOValueBuckets(drawing?.drawItems?.LO || []),
        [drawing]
    );

    const loColumnPairs = useMemo(
        () => normalizeLOColumnPairs(drawing?.drawItems?.LO || []),
        [drawing]
    );

    const postMap = useMemo(() => {
        return posts.reduce((acc, item) => {
            acc[item.post] = item;
            return acc;
        }, {});
    }, [posts]);

    const aGroupPosts = useMemo(() => {
        return posts
            .filter((item) => isAGroupPost(item.post))
            .sort((a, b) => getAGroupIndex(a.post) - getAGroupIndex(b.post));
    }, [posts]);

    const layout = getResultLayout(lotteryType, isNight);
    const type = drawing.shiftCode === "kp16" || drawing.shiftCode === "kp18" ? "KP_MC" : lotteryType;
    const background = getResultBackground(type, isNight, brand);

    if (!drawing || !layout) return null;

    const dateTypography = resolvePlatformTypography(
        layout.textStyle?.date,
        platformKey
    );
    const timeTypography = resolvePlatformTypography(
        layout.textStyle?.time,
        platformKey
    );

    const renderPostRows = (rawSection) => {
        const section = resolvePlatformTypography(rawSection, platformKey);
        const groups = section.groups?.length
            ? section.groups
            : [
                {
                    codes: section.codes,
                    startTop: section.rowTemplate.startTop,
                    rowHeight: section.rowTemplate.rowHeight,
                },
            ];

        const fallbackRows = section.codes?.length
            ? section.codes.map((code) => postMap[code]).filter(Boolean)
            : posts.filter((item) =>
                section.excludeAGroup ? !isAGroupPost(item.post) : true
            );

        const rows = groups.flatMap((group, groupIndex) => {
            const groupRows = group.codes?.length
                ? group.codes.map((code) => postMap[code]).filter(Boolean)
                : fallbackRows;

            return groupRows.map((row, rowIndex) => (
                <OverlayResultBox
                    key={`${section.key}-${groupIndex}-${row.post || rowIndex}`}
                    pos={{
                        top: group.startTop + rowIndex * group.rowHeight,
                        left: section.frame.left,
                        width: section.frame.width,
                        height: group.rowHeight,
                    }}
                    type={
                        (section.valueKeys?.length || 0) > 2
                            ? "value-row"
                            : "row"
                    }
                    values={(section.valueKeys || [
                        "twoDigit",
                        "threeDigit",
                    ]).map((key) => row[key] || "")}
                    valueColors={section.valueColors}
                    twoD={row.twoDigit}
                    threeD={row.threeDigit}
                    twoDColor={section.twoDColor}
                    threeDColor={section.threeDColor}
                    fontSize={section.fontSize}
                    offsetY={section.offsetY}
                    fontFamily={section.fontFamily}
                    fontWeight={section.fontWeight}
                    letterSpacing={section.letterSpacing}
                    textShadow={section.textShadow}
                    columnGap={section.columnGap}
                    valueColumns={section.valueColumns}
                />
            ));
        });

        return rows;
    };

    const renderSection = (rawSection) => {
        const section = resolvePlatformTypography(rawSection, platformKey);

        switch (section.kind) {
            case "a-group": {
                if (!aGroupPosts.length) return null;

                const twoDItems = selectAGroupItems(
                    aGroupPosts,
                    section.twoDCodes,
                    section.twoDColumns || 4
                );
                const threeDItems = selectAGroupItems(
                    aGroupPosts,
                    section.threeDCodes,
                    section.threeDColumns || 3
                );

                const twoDList = twoDItems
                    .map((item) => item.twoDigit);
                const threeDList = threeDItems
                    .map((item) => item.threeDigit);

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="a-group"
                        twoDList={twoDList}
                        threeDList={threeDList}
                        twoDColumns={section.twoDColumns}
                        threeDColumns={section.threeDColumns}
                        twoDColor={section.twoDColor}
                        threeDColor={section.threeDColor}
                        twoDGap={section.twoDGap}
                        threeDGap={section.threeDGap}
                        twoDHeight={section.twoDHeight}
                        threeDHeight={section.threeDHeight}
                        rowGap={section.rowGap}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                        twoDFontSize={section.twoDFontSize}
                        threeDFontSize={section.threeDFontSize}
                    />
                );
            }

            case "post-rows":
                return renderPostRows(section);

            case "multi-row": {
                const items = section.codes.map((code) => {
                    const item = postMap[code];

                    return {
                        label: code,
                        twoD: item?.twoDigit || "",
                        threeD: item?.threeDigit || "",
                    };
                });

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="multi-row"
                        items={items}
                        groupColumns={section.groupColumns}
                        itemColumns={section.itemColumns}
                        groupGap={section.groupGap}
                        itemGap={section.itemGap}
                        className={section.className}
                        twoDColor={section.twoDColor}
                        threeDColor={section.threeDColor}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                    />
                );
            }

            case "pair-column": {
                const items = section.codes
                    .map((code) => postMap[code])
                    .filter(Boolean)
                    .map(toPairItem);

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="pair-column"
                        items={items}
                        rows={section.rows}
                        rowGap={section.rowGap}
                        columnGap={section.columnGap}
                        className={section.className}
                        twoDColor={section.twoDColor}
                        threeDColor={section.threeDColor}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                        fontFamily={section.fontFamily}
                        fontWeight={section.fontWeight}
                        letterSpacing={section.letterSpacing}
                        textShadow={section.textShadow}
                    />
                );
            }

            case "pair-list-range": {
                const items = loItems
                    .filter((item) => {
                        const value = getLoNumber(item.post);
                        return value >= section.start && value <= section.end;
                    })
                    .sort((a, b) => getLoNumber(a.post) - getLoNumber(b.post))
                    .map(toPairItem);

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="pair-list"
                        items={items}
                        rowGap={section.rowGap}
                        columnGap={section.columnGap}
                        rowHeight={section.rowHeight}
                        className={section.className}
                        twoDColor={section.twoDColor}
                        threeDColor={section.threeDColor}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                        fontFamily={section.fontFamily}
                        fontWeight={section.fontWeight}
                        letterSpacing={section.letterSpacing}
                        textShadow={section.textShadow}
                    />
                );
            }

            case "pair-list-column": {
                const items = (loColumnPairs[String(section.columnNumber)] || [])
                    .map(toPairItem);

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="pair-list"
                        items={items}
                        rowGap={section.rowGap}
                        columnGap={section.columnGap}
                        rowHeight={section.rowHeight}
                        className={section.className}
                        twoDColor={section.twoDColor}
                        threeDColor={section.threeDColor}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                        fontFamily={section.fontFamily}
                        fontWeight={section.fontWeight}
                        letterSpacing={section.letterSpacing}
                        textShadow={section.textShadow}
                    />
                );
            }

            case "value-grid": {
                const sourceItemsByValueKey = {
                    twoDigit: loValueBuckets.twoD,
                    threeDigit: loValueBuckets.threeD,
                };
                const sourceItems =
                    sourceItemsByValueKey[section.valueKey] || loItems;
                const arrangedItems = toVerticalColumns(
                    sourceItems,
                    section.columns
                );

                const values = arrangedItems.map((item) => ({
                    post: item.post,
                    value: item[section.valueKey],
                }));

                return (
                    <OverlayResultBox
                        key={section.key}
                        pos={section.pos}
                        type="value-grid"
                        values={values}
                        rowGap={section.rowGap}
                        columnGap={section.columnGap}
                        columns={section.columns}
                        rowHeight={section.rowHeight}
                        textColor={section.textColor}
                        fontSize={section.fontSize}
                        offsetY={section.offsetY}
                        fontFamily={section.fontFamily}
                        fontWeight={section.fontWeight}
                        letterSpacing={section.letterSpacing}
                        lineHeight={section.lineHeight}
                        textShadow={section.textShadow}
                    />
                );
            }

            default:
                return null;
        }
    };

    return (
        <div className="result-image" style={{ aspectRatio: layout.ratio }}>
            <img crossOrigin="anonymous" src={background} className="result-bg" alt="" />

            <OverlayText
                pos={layout.zones.date}
                className="overlay-date"
                fontSize={dateTypography?.fontSize}
                typography={dateTypography}
            >
                {getDate(drawing.drawAt)}
            </OverlayText>

            <OverlayText
                pos={layout.zones.time}
                className="overlay-time"
                fontSize={timeTypography?.fontSize}
                typography={timeTypography}
            >
                {format12h(drawing.drawAt)}
            </OverlayText>

            {layout.sections.map(renderSection)}
        </div>
    );
}
